import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from 'react';

import { toast } from 'react-toastify';

import api from '~/services/api';

type DataSheets = {
  id: number;
  student_id: number;
  professional_id: number;
  gym_id?: string;
  genre?: string;
  classification: string;
  objective: string;
  student_level: string;
  frequency: string;
  available_time: string;
  training_division?: string;
  week_days: string;
  health_history: string;
  injury_history: string;
  height: number;
  weight: number;
  goal_weight: number;
  body_fat: number;
  imc: number;
  birth_date: string;
  circumference_chest: number;
  circumference_waist: number;
  circumference_abdomen: number;
  circumference_hip: number;
  skin_folds_total: number;
  created_at: string;
  updated_at: string;
  time_preference: string;
  circumference_relaxed_arm_right: number;
  circumference_relaxed_arm_left: number;
  circumference_contracted_arm_right: number;
  circumference_contracted_arm_left: number;
  circumference_forearm_right: number;
  circumference_forearm_left: number;
  circumference_fist_right: number;
  circumference_fist_left: number;
  circumference_proximal_thigh_right: number;
  circumference_proximal_thigh_left: number;
  circumference_distal_thigh_right: number;
  circumference_distal_thigh_left: number;
  circumference_calf_right: number;
  circumference_calf_left: number;
  skin_folds_biceps_right: number;
  skin_folds_biceps_left: number;
  skin_folds_triceps_right: number;
  skin_folds_triceps_left: number;
  skin_folds_subscapular_right: number;
  skin_folds_subscapular_left: number;
  skin_folds_middle_armpit_right: number;
  skin_folds_middle_armpit_left: number;
  skin_folds_heigsupra_iliacaht_right: number;
  skin_folds_heigsupra_iliacaht_left: number;
  skin_folds_abdomen_right: number;
  skin_folds_abdomen_left: number;
  skin_folds_breastplate_right: number;
  skin_folds_breastplate_left: number;
  skin_folds_thigh_right: number;
  skin_folds_thigh_left: number;
  skin_folds_calf_right: number;
  skin_folds_calf_left: number;
};

type DataSheetsData = {
  dataSheetsList: DataSheets[];
  setDataSheets: (data: DataSheets[]) => void;
  deleteDataSheet: (id: string | number) => void;
  loadDataSheets: (studentId: string) => void;
};

export const DataSheetsContext = createContext({} as DataSheetsData);

type DataSheetsProviderProps = {
  children: ReactNode;
};

export function DataSheetsProvider({ children }: DataSheetsProviderProps) {
  const [dataSheetsList, setDataSheetsList] = useState<DataSheets[]>([]);

  function setDataSheets(dataSheets: DataSheets[]) {
    setDataSheetsList(dataSheets);
  }

  async function deleteDataSheet(id: string | number) {
    try {
      await api.delete(`datasheets/${id}`);

      setDataSheetsList(
        dataSheetsList.filter((dataSheet) => dataSheet.id !== +id),
      );

      toast.success('Ficha técnica deletada com sucesso!');
    } catch (err) {
      console.log(err);

      toast.error('Erro ao deletar Ficha técnica!');
    }
  }

  const loadDataSheets = useCallback(async (studentId: string) => {
    const { data } = await api.get(`students/${studentId}/datasheets`);

    setDataSheetsList(data);
  }, []);

  return (
    <DataSheetsContext.Provider
      value={{
        dataSheetsList,
        setDataSheets,
        deleteDataSheet,
        loadDataSheets,
      }}
    >
      {children}
    </DataSheetsContext.Provider>
  );
}

export const useDataSheets = (): DataSheetsData => {
  const context = useContext(DataSheetsContext);

  if (!context) {
    throw new Error(`useDataSheets must be use within a DataSheetsProvider!`);
  }

  return context;
};
